import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, paymentRequired, type ImportPreviewResponse } from '../api/endpoints';
import { ApiException } from '../api/client';
import { UpgradeModal } from '../components/UpgradeModal';

export function ImportRecipeScreen() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [isShared, setIsShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upsell, setUpsell] = useState<{ requiredTier: 'pro' | 'family'; message: string } | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  async function runPreview(e: FormEvent) {
    e.preventDefault();
    if (!url.trim() || loading) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      setPreview(await endpoints.importPreview(url.trim()));
    } catch (err) {
      setError(err instanceof ApiException ? err.body.message : 'Не удалось импортировать рецепт');
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    if (!preview || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { recipeId } = await endpoints.importConfirm({
        url: url.trim(),
        recipe: preview.recipe,
        isShared,
      });
      setSavedId(recipeId);
      setSaving(false);
    } catch (err) {
      const pr = paymentRequired(err);
      if (pr) {
        setUpsell(pr);
        setSaving(false);
        return;
      }
      setError(err instanceof ApiException ? err.body.message : 'Не удалось сохранить рецепт');
      setSaving(false);
    }
  }

  const extraNames = new Set((preview?.extra ?? []).map((e) => e.name));

  // Ошибку показываем там, где сейчас фокус пользователя: на стадии ввода — под формой,
  // на стадии превью — рядом с «Сохранить» (иначе ошибка confirm на длинном рецепте
  // уезжает выше вьюпорта). Стадии взаимоисключающие: при превью preview != null.
  const errorBox = error ? (
    <p
      role="alert"
      className="mt-4 rounded-xl bg-primary-50 px-4 py-3 text-sm text-primary-700 ring-1 ring-inset ring-primary-100"
    >
      {error}
    </p>
  ) : null;

  return (
    <div className="mx-auto min-h-screen max-w-md bg-cream px-4 pb-10 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          aria-label="Назад"
          className="rounded-full bg-white/90 px-3 py-1.5 text-ink shadow"
        >
          ←
        </button>
        <h1 className="text-xl font-bold text-ink">Рецепт по ссылке</h1>
      </div>

      {savedId && (
        <div className="mt-6 rounded-2xl bg-white p-5 shadow-card">
          <p className="font-semibold text-ink">Рецепт сохранён ✓</p>
          <p className="mt-1 text-sm text-ink/60">Можно открыть его или добавить ещё один.</p>
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => navigate(`/recipe/${savedId}`)}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card active:scale-[0.98]"
            >
              Открыть рецепт
            </button>
            <button
              onClick={() => {
                setSavedId(null);
                setPreview(null);
                setUrl('');
                setError(null);
                setIsShared(false);
              }}
              className="flex-1 rounded-xl border border-ink/15 px-4 py-3 font-semibold text-ink/70"
            >
              Импортировать ещё
            </button>
          </div>
        </div>
      )}

      {!savedId && (
      <form onSubmit={runPreview} className="mt-5">
        <label htmlFor="import-url" className="text-sm font-medium text-ink/70">
          Вставьте ссылку на рецепт
        </label>
        <input
          id="import-url"
          type="url"
          inputMode="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://povar.ru/recipes/..."
          className="mt-1.5 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink outline-none focus:border-primary-500"
        />
        <button
          type="submit"
          disabled={!url.trim() || loading}
          className="mt-3 w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card transition active:scale-[0.98] disabled:opacity-50"
        >
          {loading ? 'Распознаём…' : 'Распознать'}
        </button>
      </form>
      )}

      {!savedId && !preview && errorBox}

      {!savedId && preview && (
        <div className="stagger mt-6">
          <RecipePreview preview={preview} extraNames={extraNames} />

          {preview.warnings.length > 0 && (
            <ul className="mt-3 space-y-1">
              {preview.warnings.map((w, i) => (
                <li key={i} className="text-sm text-ink/60">
                  • {w}
                </li>
              ))}
            </ul>
          )}

          <label className="mt-5 flex items-center gap-2 text-sm text-ink/80">
            <input
              type="checkbox"
              checked={isShared}
              onChange={(e) => setIsShared(e.target.checked)}
              className="h-4 w-4 accent-primary-600"
            />
            Поделиться с общей базой
          </label>

          {errorBox}

          <div className="mt-4 flex gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-card transition active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="rounded-xl border border-ink/15 px-4 py-3 font-semibold text-ink/70"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {upsell && <UpgradeModal requiredTier={upsell.requiredTier} message={upsell.message} onClose={() => setUpsell(null)} />}
    </div>
  );
}

function RecipePreview({
  preview,
  extraNames,
}: {
  preview: ImportPreviewResponse;
  extraNames: Set<string>;
}) {
  const r = preview.recipe;
  const k = r.kbjuPerServing;
  const meta = [
    r.cookTimeMin ? `${r.cookTimeMin} мин` : null,
    r.servings ? `${r.servings} порц.` : null,
    k.kcal ? `${k.kcal} ккал` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rounded-2xl bg-white p-4 shadow-card">
      <h2 className="text-lg font-bold text-ink">{r.title}</h2>
      {meta && <p className="mt-1 text-sm text-ink/60">{meta}</p>}
      <p className="mt-2 text-xs text-ink/50">
        Сопоставлено с каталогом: {preview.matchedCount} из {r.ingredients.length}
      </p>

      <h3 className="mb-2 mt-4 font-semibold text-ink">Ингредиенты</h3>
      <ul className="space-y-1">
        {r.ingredients.map((ing, i) => {
          const extra = extraNames.has(ing.name);
          return (
            <li key={i} className="flex items-center justify-between">
              <span className={extra ? 'font-medium text-primary-600' : 'text-ink'}>
                {ing.name}
                {ing.isOptional ? ' (по желанию)' : ''}
              </span>
              <span className="text-sm text-ink/50">
                {ing.amount ?? ''} {ing.unit ?? ''}
                {extra ? ' · нет в каталоге' : ''}
              </span>
            </li>
          );
        })}
      </ul>

      <h3 className="mb-2 mt-4 font-semibold text-ink">Приготовление</h3>
      <ol className="list-decimal space-y-2 pl-5">
        {r.steps.map((s, i) => (
          <li key={i} className="text-ink/90">
            {s}
          </li>
        ))}
      </ol>

      <div className="mt-4 grid grid-cols-4 gap-2 text-center">
        {(
          [
            ['Ккал', k.kcal],
            ['Белки', k.protein],
            ['Жиры', k.fat],
            ['Углеводы', k.carbs],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="rounded-xl bg-cream p-2">
            <p className="text-sm font-semibold text-ink">{value}</p>
            <p className="text-xs text-ink/50">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
